import { useMemo } from 'react';
import { AreaLineChart } from '@/components/charts';
import type { TrafficPoint } from '@/lib/api';
import { formatBitrate, formatClock } from '@/lib/format';

/**
 * Verlauf der Datenraten auf Basis des Vela-Liniendiagramms.
 *
 * Das Diagramm nimmt reine Zahlenreihen entgegen, daher werden die Messpunkte
 * hier auf zwei Reihen reduziert. Bei sehr vielen Punkten wird gleichmaessig
 * ausgeduennt, damit die Kurve lesbar bleibt.
 */
export function TrafficChart({
  points,
  direction,
  height = 240,
}: {
  points: TrafficPoint[];
  /** rx = Upload der Clients, tx = Download der Clients. */
  direction: 'rx' | 'tx';
  height?: number;
}) {
  const { values, labels } = useMemo(() => {
    const maxPoints = 60;
    const step = Math.max(1, Math.ceil(points.length / maxPoints));
    const sampled = points.filter((_, index) => index % step === 0);

    return {
      values: sampled.map((point) =>
        direction === 'rx' ? point.rxBitsPerSecond : point.txBitsPerSecond,
      ),
      labels: sampled.map((point) => formatClock(point.timestamp)),
    };
  }, [points, direction]);

  if (values.length < 2) {
    return (
      <div
        className="flex items-center justify-center rounded-[var(--radius-vela-md)] border border-dashed border-line text-[13px] text-t2"
        style={{ height }}
      >
        Noch nicht genug Messwerte fuer einen Verlauf.
      </div>
    );
  }

  return (
    <AreaLineChart
      data={values}
      labels={labels}
      height={height}
      color={direction === 'rx' ? 'var(--acc)' : 'var(--info)'}
      formatValue={(value) => formatBitrate(value)}
    />
  );
}
